/**
 * Analytics Tracking Utilities
 *
 * Uses Google Tag Manager (GTM) dataLayer for form tracking.
 *
 * Usage:
 *   import { trackFormSubmissionGTM } from "@/lib/analytics";
 *
 *   // Form submission
 *   trackFormSubmissionGTM("contact_form", true);
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Array<Record<string, unknown> | IArguments | unknown[]>;
  }
}

// ============================================================================
// Internal GA4 helpers - used by trackFormSubmissionGTM for backwards compat
// ============================================================================

function isAnalyticsAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

// Renamed to exporting function to avoid conflict with internal helpers if any, 
// and to make it clear this is the public API.
export function trackCustomEvent(
  eventName: string,
  parameters?: Record<string, string | number | boolean | undefined>
): void {
  // 1. Push to GTM DataLayer
  pushToDataLayer(eventName, parameters);

  // 2. Push to GA4 gtag (backwards compat / direct implementation)
  if (isAnalyticsAvailable()) {
    window.gtag?.("event", eventName, parameters);
  } else if (process.env.NODE_ENV === "development") {
    console.log("[Analytics Debug]", eventName, parameters);
  }
}

// ============================================================================
// Google Tag Manager (GTM) / DataLayer Functions
// ============================================================================

function isGTMAvailable(): boolean {
  return typeof window !== "undefined" && Array.isArray(window.dataLayer);
}

function pushToDataLayer(
  event: string,
  data?: Record<string, string | number | boolean | undefined>
): void {
  if (!isGTMAvailable()) {
    // Debug logging handled in trackCustomEvent mainly, but kept here for safety 
    // if accessed directly internally.
    return;
  }

  window.dataLayer?.push({
    event,
    ...data,
  });
}
// ...existing code...

/**
 * Track form submissions via GTM dataLayer
 * Sends to both GTM dataLayer and GA4 gtag for maximum compatibility
 */
export function trackFormSubmissionGTM(
  formName: string,
  success: boolean,
  additionalData?: Record<string, string | number | boolean>
): void {
  trackCustomEvent("form_submission", {
    form_name: formName,
    form_success: success,
    ...additionalData,
  });
}

/**
 * Track generic clicks (links, buttons, etc.)
 */
export function trackClick(
  label: string,
  destination: string,
  category: string = "cta"
): void {
  trackCustomEvent("click", {
    event_category: category,
    event_label: label,
    link_url: destination,
  });
}
